import { Link } from 'react-router-dom';
import { Seo, Mark, Container } from '../components/marketing/Primitives';

/* Five audiences, held to the same weight. No audience owns the company. */

const audiences = [
  {
    id: 'boards',
    who: 'Certification boards',
    pain: 'Volunteer item-writers are exhausted, and every published item must survive challenge.',
    gives:
      'Your designated sources and your blueprint, operationalized into certification-grade items with 87% less expert time. Every consequential decision stays with an expert physician, and every item carries a provable chain from source to delivered word. Zero AI-attributed factual errors in production; zero validity challenges from regulators or accreditors.',
    links: [{ label: 'EdAI Suites', to: '/products/suites' }],
    cta: { label: 'Talk to us about your board', to: '/contact' },
  },
  {
    id: 'schools',
    who: 'Medical schools',
    pain: 'Accreditors now expect evidence of how students work with clinical AI, and OSCE capacity is expensive.',
    gives:
      'Teach and assess against the same validated knowledge your graduates will be licensed and certified on. PreRounds as the curricular spine, documented clinical AI competency for accreditation, and simulated patient encounters with independent evaluator scoring, without standardized-patient scheduling and cost.',
    links: [
      { label: 'EdAI MedSchool', to: '/products/medschool' },
      { label: 'Practicums', to: '/products/practicums' },
    ],
    cta: { label: 'Bring it to your school', to: '/contact' },
  },
  {
    id: 'candidates',
    who: 'Licensure and certification candidates',
    pain: 'Generic question banks are built for a population average, not for what your board actually requires.',
    gives:
      'Preparation scoped to what you are accountable for: the sources your board designates, the blueprint your jurisdiction publishes. Spaced repetition and concept mastery tracking focus every session, calibrated just above your current capability.',
    links: [{ label: 'EdAI Prep', to: '/products/prep' }],
    cta: { label: 'Start preparing', to: '/products/prep' },
  },
  {
    id: 'img',
    who: 'Internationally trained physicians and their sponsors',
    pain: 'A decision worth years and thousands of dollars, made on fragmented and optimistic information.',
    gives:
      'Every route to your destination, side by side, sourced to the regulator: time, cost, exams, and outcome, including the routes you do not qualify for and why. Then a personalized, dependency-ordered plan with a document vault and deadline reminders. Employers get a defensible view of when a candidate can actually practice.',
    links: [{ label: 'Pathways', to: '/products/pathways' }],
    cta: { label: 'Compare your routes', to: '/products/pathways' },
  },
  {
    id: 'learners',
    who: 'Self-directed learners, any domain',
    pain: 'Your own material is the right material. Turning it into study tools by hand eats the study time.',
    gives:
      'The modality engine on its own: upload a lecture deck, a chapter, or your notes, and get practice questions with explanations, a study guide, and Anki-ready flashcards, tuned to your exam. Domain-agnostic by design: medicine today, law, engineering, and any professional certification tomorrow.',
    links: [{ label: 'EdAI Express', to: '/products/express' }],
    cta: { label: 'Try it with your own material', to: '/products/express' },
  },
];

export default function AudiencesPage() {
  return (
    <>
      <Seo
        title="Who it's for"
        description="EdAI serves five audiences from one validated corpus: certification boards, medical schools, candidates, internationally trained physicians and their sponsors, and self-directed learners in any domain."
      />

      <section className="border-b border-hairline bg-surface">
        <Container className="py-20 md:py-24">
          <Mark>Who it's for</Mark>
          <h1 className="mt-4 max-w-[22ch] font-serif text-4xl leading-tight md:text-5xl">
            One corpus. Five audiences. The same standard.
          </h1>
          <p className="mt-5 max-w-measure-wide text-lg leading-relaxed text-slate">
            The Standard of Knowledge serves everyone it holds accountable: the board that defends
            the exam, the school that prepares for it, the candidate who sits it, the physician
            crossing a border to practice, and the learner with their own material in hand.
          </p>
        </Container>
      </section>

      <section className="py-16 md:py-20">
        <Container className="space-y-14">
          {audiences.map((a) => (
            <article
              key={a.id}
              id={a.id}
              className="grid gap-8 border-b border-hairline pb-14 last:border-b-0 last:pb-0 md:grid-cols-[1fr_1.8fr] md:gap-16"
            >
              <div>
                <h2 className="font-serif text-2xl leading-snug md:text-3xl">{a.who}</h2>
                <p className="mt-3 text-sm italic leading-relaxed text-slate">{a.pain}</p>
              </div>
              <div>
                <p className="leading-relaxed text-slate">{a.gives}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <Link to={a.cta.to} className="btn-primary">{a.cta.label}</Link>
                  {a.links.map((l) => (
                    <Link key={l.to} to={l.to} className="link-quiet text-sm font-semibold">
                      {l.label}
                    </Link>
                  ))}
                </div>
              </div>
            </article>
          ))}
        </Container>
      </section>
    </>
  );
}
